import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const RegisterFormSection = () => {
  const { t } = useTranslation('choHem');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    kioskType: '',
    product: '',
    addonPlan: '',
    message: '',
  });
  const [extras, setExtras] = useState<Record<string, boolean>>({
    selfEmployed: false,
    taxFiling: false,
    facebook: false,
    kioskDesign: false,
    kioskSign: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const extrasList = [
    { key: 'selfEmployed', label: t('register.form.extraSelfEmployed') },
    { key: 'taxFiling', label: t('register.form.extraTaxFiling') },
    { key: 'facebook', label: t('register.form.extraFacebook') },
    { key: 'kioskDesign', label: t('register.form.extraKioskDesign') },
    { key: 'kioskSign', label: t('register.form.extraKioskSign') },
  ];

  const handleExtrasChange = (key: string) => {
    setExtras((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const selectedExtras = extrasList
        .filter((ex) => extras[ex.key])
        .map((ex) => ex.label)
        .join(', ');

      const notesWithExtras = [
        formData.message,
        selectedExtras ? `Dịch vụ bổ sung: ${selectedExtras}` : '',
      ]
        .filter(Boolean)
        .join('\n');

      const payload = {
        full_name: formData.name,
        phone: formData.phone,
        email: formData.email,
        kiosk_type: formData.kioskType,
        product_category: formData.product,
        addon_plan: formData.addonPlan,
        notes: notesWithExtras,
      };

      const res = await fetch('https://jcoaewcaljzcbikjosgg.supabase.co/functions/v1/kiosk-registration-webhook', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', phone: '', email: '', kioskType: '', product: '', addonPlan: '', message: '' });
        setExtras({ selfEmployed: false, taxFiling: false, facebook: false, kioskDesign: false, kioskSign: false });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-14 md:py-24 bg-gradient-to-b from-gray-950 to-black" id="register">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-start">
          {/* Left info */}
          <div className="text-white">
            <span className="inline-block px-3 py-1 bg-amber-500/15 border border-amber-400/30 rounded-full text-xs text-amber-300 font-semibold uppercase tracking-wider mb-4">
              {t('register.badge')}
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold font-serif mb-4 leading-tight">
              {t('register.title.line1')} <br />
              <span className="text-amber-400">{t('register.title.line2')}</span>
            </h2>
            <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-8">
              {t('register.subtitle')}
            </p>

            <div className="space-y-4">
              {[
                { icon: 'ri-map-pin-2-line', title: t('register.info.location.title'), desc: t('register.info.location.value') },
                { icon: 'ri-time-line', title: t('register.info.hours.title'), desc: t('register.info.hours.value') },
                { icon: 'ri-phone-line', title: t('register.info.hotline.title'), desc: t('register.info.hotline.value') },
                { icon: 'ri-mail-line', title: t('register.info.email.title'), desc: t('register.info.email.value') },
              ].map((info, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-500/15 rounded-lg flex-shrink-0">
                    <i className={`${info.icon} text-amber-400 text-base`}></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">{info.title}</p>
                    <p className="text-gray-400 text-xs md:text-sm">{info.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-amber-500/10 border border-amber-400/20 rounded-2xl">
              <p className="text-amber-300 font-bold text-sm mb-1">{t('register.promo.title')}</p>
              <p className="text-gray-300 text-xs md:text-sm">
                {t('register.promo.desc')}
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 md:p-8">
            <h3 className="text-white font-bold text-lg md:text-xl mb-5">{t('register.form.title')}</h3>

            <form id="kiosk-register-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
              {/* Name & Phone */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1.5">
                    {t('register.form.name')} <span className="text-red-400">{t('register.form.required')}</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t('register.form.namePlaceholder')}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400/60 focus:bg-white/15 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1.5">
                    {t('register.form.phone')} <span className="text-red-400">{t('register.form.required')}</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder={t('register.form.phonePlaceholder')}
                    className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400/60 focus:bg-white/15 transition-all"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5">
                  {t('register.form.email')} <span className="text-red-400">{t('register.form.required')}</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder={t('register.form.emailPlaceholder')}
                  className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400/60 focus:bg-white/15 transition-all"
                />
              </div>

              {/* Kiosk Type — grouped by daily / monthly */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5">
                  {t('register.form.kioskType')}
                </label>
                <select
                  name="kioskType"
                  value={formData.kioskType}
                  onChange={(e) => setFormData({ ...formData, kioskType: e.target.value })}
                  className="w-full px-3 py-2.5 bg-gray-900 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400/60 transition-all cursor-pointer"
                >
                  <option value="">{t('register.form.kioskTypePlaceholder')}</option>
                  <optgroup label={t('register.form.kioskGroup.daily')}>
                    <option value={t('register.form.kioskMiniDaily')}>{t('register.form.kioskMiniDaily')}</option>
                    <option value={t('register.form.kioskStandardDaily')}>{t('register.form.kioskStandardDaily')}</option>
                  </optgroup>
                  <optgroup label={t('register.form.kioskGroup.monthly')}>
                    <option value={t('register.form.kioskMiniMonthly')}>{t('register.form.kioskMiniMonthly')}</option>
                    <option value={t('register.form.kioskStandardMonthly')}>{t('register.form.kioskStandardMonthly')}</option>
                  </optgroup>
                  <optgroup label="🏢">
                    <option value={t('register.form.kioskOfficeRent')}>{t('register.form.kioskOfficeRent')}</option>
                  </optgroup>
                </select>
              </div>

              {/* Product / Service */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5">
                  {t('register.form.product')}
                </label>
                <select
                  name="product"
                  value={formData.product}
                  onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                  className="w-full px-3 py-2.5 bg-gray-900 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400/60 transition-all cursor-pointer"
                >
                  <option value="">{t('register.form.productPlaceholder')}</option>
                  <option value={t('register.form.productCraft')}>{t('register.form.productCraft')}</option>
                  <option value={t('register.form.productClothing')}>{t('register.form.productClothing')}</option>
                  <option value={t('register.form.productJewelry')}>{t('register.form.productJewelry')}</option>
                  <option value={t('register.form.productKitchen')}>{t('register.form.productKitchen')}</option>
                  <option value={t('register.form.productService')}>{t('register.form.productService')}</option>
                  <option value={t('register.form.productOther')}>{t('register.form.productOther')}</option>
                </select>
              </div>

              {/* Addon Support Package */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-service-line text-amber-400"></i>
                    {t('register.form.addonTitle')}
                  </span>
                </label>
                <select
                  name="addonPlan"
                  value={formData.addonPlan}
                  onChange={(e) => setFormData({ ...formData, addonPlan: e.target.value })}
                  className="w-full px-3 py-2.5 bg-gray-900 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400/60 transition-all cursor-pointer"
                >
                  <option value="">{t('register.form.addonPlaceholder')}</option>
                  <option value={t('register.form.addonNone')}>{t('register.form.addonNone')}</option>
                  <option value={t('register.form.addonStarter')}>{t('register.form.addonStarter')}</option>
                  <option value={t('register.form.addonBusiness')}>{t('register.form.addonBusiness')}</option>
                  <option value={t('register.form.addonPremium')}>{t('register.form.addonPremium')}</option>
                </select>
              </div>

              {/* Optional Extras — checkboxes */}
              <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                <p className="text-xs font-medium text-gray-300 mb-0.5 flex items-center gap-1.5">
                  <i className="ri-add-circle-line text-amber-400"></i>
                  {t('register.form.extrasTitle')}
                </p>
                <p className="text-[11px] text-gray-500 mb-3">{t('register.form.extrasSubtitle')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {extrasList.map((ex) => (
                    <label
                      key={ex.key}
                      className="flex items-center gap-2.5 cursor-pointer group"
                    >
                      <div
                        onClick={() => handleExtrasChange(ex.key)}
                        className={`w-4 h-4 flex-shrink-0 rounded border transition-all cursor-pointer flex items-center justify-center ${
                          extras[ex.key]
                            ? 'bg-amber-500 border-amber-500'
                            : 'bg-white/10 border-white/30 group-hover:border-amber-400/60'
                        }`}
                      >
                        {extras[ex.key] && <i className="ri-check-line text-black text-[10px]"></i>}
                      </div>
                      <span
                        onClick={() => handleExtrasChange(ex.key)}
                        className={`text-xs transition-colors ${extras[ex.key] ? 'text-white' : 'text-gray-400 group-hover:text-gray-300'}`}
                      >
                        {ex.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1.5">
                  {t('register.form.message')}
                </label>
                <textarea
                  name="message"
                  rows={3}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder={t('register.form.messagePlaceholder')}
                  className="w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 text-sm focus:outline-none focus:border-amber-400/60 focus:bg-white/15 transition-all resize-none"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1 text-right">{formData.message.length}{t('register.form.messageLimit')}</p>
              </div>

              {submitStatus === 'success' && (
                <div className="p-3 bg-emerald-500/15 border border-emerald-400/30 rounded-lg text-sm text-emerald-300">
                  {t('register.success')}
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="p-3 bg-red-500/15 border border-red-400/30 rounded-lg text-sm text-red-300">
                  {t('register.error')}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || formData.message.length > 500}
                className="w-full py-3.5 bg-amber-500 text-black font-bold rounded-lg hover:bg-amber-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-sm md:text-base"
              >
                {isSubmitting ? t('register.form.submitting') : t('register.form.submit')}
              </button>

              <p className="text-center text-gray-500 text-xs">
                {t('register.note')}
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RegisterFormSection;
